import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

const en = (text: string) => ({ en: text, tr: '' })

const content: CompliancePageContent = {
  path: '/compliance/dora',
  bilingual: false,
  seoTitle: en('DORA Compliance: Identity & Access Controls | Monofor'),
  seoDescription: en(
    'Meet DORA ICT risk-management requirements with Monofor: strong authentication, privileged access management, third-party access control, and audit-ready evidence.'
  ),
  heroEyebrow: en('DORA compliance'),
  heroTitle: en('DORA-ready identity controls for financial entities.'),
  heroBody: en(
    'The Digital Operational Resilience Act has applied to EU financial entities since January 2025, and identity is where most of its ICT risk-management articles land. Monofor enforces strong authentication, disciplined privileged access, and controlled third-party access, then produces the evidence your supervisors expect.'
  ),
  contextHeading: en('Where DORA meets identity'),
  contextBody: en(
    'DORA (Regulation (EU) 2022/2554) requires banks, insurers, investment firms, crypto-asset providers, and their critical ICT third parties to manage ICT risk with documented, testable controls. Article 9 obliges firms to use strong authentication mechanisms and policies that limit access to ICT assets to what is legitimate and approved; Article 10 expects anomalous activity to be detected; and the incident-reporting chapter assumes you can reconstruct who did what, where, when.\n\nSupervisors do not audit intentions; they audit evidence. That makes the identity layer the fastest place to demonstrate DORA progress: enforce MFA everywhere, put privileged sessions under recording, time-bound vendor access, and export the audit trail.'
  ),
  requirementsHeading: en('Article to control, mapped.'),
  requirementsBody: en(
    'The identity-related obligations that recur across DORA’s ICT risk-management chapter, and the Monofor capability that answers each.'
  ),
  requirements: [
    {
      requirement: en('Strong authentication (Art. 9)'),
      detail: en(
        'Firms must implement strong authentication mechanisms based on relevant standards.'
      ),
      capability: en(
        'Monosign enforces MFA on every login (TOTP, push, and phishing-resistant FIDO2 passkeys), with adaptive step-up driven by risk signals such as new devices or unusual locations.'
      ),
      productHref: '/sign/mfa',
      productName: 'Monosign MFA',
    },
    {
      requirement: en('Access limited to legitimate need (Art. 9)'),
      detail: en(
        'Policies must restrict physical and logical access to ICT assets to approved, role-appropriate use.'
      ),
      capability: en(
        'Role-based access via SSO, joiner-mover-leaver automation that revokes access on role change, and access reviews that certify entitlements on the cadence your policy defines.'
      ),
      productHref: '/sync/governance',
      productName: 'Monosync Governance',
    },
    {
      requirement: en('Privileged access under control'),
      detail: en(
        'Elevated access to critical systems must be minimized, monitored, and reconstructable.'
      ),
      capability: en(
        'Monopam vaults privileged credentials, grants just-in-time elevation behind approvals, and records every session as video plus keystroke transcript: the reconstruction DORA incident reporting assumes.'
      ),
      productHref: '/pam',
      productName: 'Monopam',
    },
    {
      requirement: en('ICT third-party access (Ch. V)'),
      detail: en(
        'Vendor and service-provider access is squarely in scope, including for critical ICT third parties.'
      ),
      capability: en(
        'External partners get time-bound, recorded, approval-gated access through the same privileged gateway as employees: no standing VPN accounts, no shared credentials.'
      ),
      productHref: '/solutions/external-partners',
      productName: 'External & partner access',
    },
    {
      requirement: en('Detection & anomaly monitoring (Art. 10)'),
      detail: en(
        'Firms need mechanisms to promptly detect anomalous activities and single points of failure.'
      ),
      capability: en(
        'Authentication anomalies (new devices, impossible travel, brute-force patterns, dormant accounts waking up) surface as signals and feed your SIEM through structured log export.'
      ),
      productHref: '/sign',
      productName: 'Monosign',
    },
    {
      requirement: en('Operational resilience & deployment control'),
      detail: en(
        'Resilience expectations extend to where and how critical systems run.'
      ),
      capability: en(
        'Monofor deploys self-hosted in your own infrastructure or cloud tenant; identity does not become another external dependency in your DORA register unless you choose SaaS.'
      ),
      productHref: '/sign',
      productName: 'Monosign',
    },
  ],
  faqs: [
    {
      q: en('Does DORA apply to us?'),
      a: en(
        'DORA applies to most EU financial entities (credit institutions, payment and e-money institutions, investment firms, insurers, crypto-asset service providers) and to ICT third-party providers serving them. Non-EU firms serving EU financial customers are often pulled in contractually. Confirm scope with your legal team.'
      ),
    },
    {
      q: en('Does deploying Monofor make us DORA compliant?'),
      a: en(
        'No product alone achieves DORA compliance; the regulation spans governance, testing, incident reporting, and contracts. Monofor implements the identity-related ICT risk controls and produces auditable evidence for them, which is a significant portion of the Article 9-10 obligations.'
      ),
    },
    {
      q: en('How does Monofor help with DORA incident reporting?'),
      a: en(
        'Incident reports need a reconstruction of what happened. Tamper-evident authentication logs, entitlement change history, and privileged session recordings with keystroke transcripts give your incident team the who-what-when within minutes rather than days.'
      ),
    },
    {
      q: en('We are an ICT provider to banks. Does this help us?'),
      a: en(
        'Yes. Critical ICT third parties face oversight themselves, and their bank customers must control third-party access. Running your own access through recorded, time-bound sessions makes you a materially easier vendor to keep, and to audit.'
      ),
    },
  ],
}

type Props = { locale: string }

export default function DoraCompliancePage({ locale }: Props) {
  return <CompliancePage content={content} locale={locale} />
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
